import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { Image as ImageGrommet, Text, Button, Box, Spinner } from "grommet";
import { FileDrop } from "react-file-drop";
import ReactCrop, { makeAspectCrop, centerCrop } from "react-image-crop";
import { useTranslation } from "react-i18next";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

function padImageToSquare(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const size = Math.max(img.width, img.height);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, (size - img.width) / 2, (size - img.height) / 2);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = dataUrl;
  });
}

function PhotoUploader(props, ref) {
  const { t } = useTranslation();
  const {
    onChange,
    fill,
    photo,
    width,
    label,
    blackbg,
    fitWidth,
    fitHeight,
    disabled = false,
  } = props;
  const [image, setImage] = useState(photo);
  const [dropping, setDropping] = useState(null);
  const [cropping, setCropping] = useState(null);
  const fileInput = useRef(null);
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 100,
    aspect: fitWidth / fitHeight,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [file, setFile] = useState();
  const [aspect] = useState(fitWidth / fitHeight);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setImage(photo);
    setCompletedCrop(null);
  }, [photo]);

  const cropImage = () => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;

    if (!(image instanceof HTMLImageElement)) {
      console.error("image is not an HTMLImageElement");
      return;
    }

    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    var newWidth = crop.width,
      newHeight = crop.height;
    if (newWidth > fitWidth || newHeight > fitHeight) {
      var aspectRatio = fitWidth / fitHeight;
      if (aspectRatio > 1) {
        newWidth = fitWidth;
        newHeight = Math.round(newWidth / aspectRatio);
      } else {
        newHeight = fitHeight;
        newWidth = Math.round(newHeight * aspectRatio);
      }
    }

    canvas.width = newWidth * pixelRatio;
    canvas.height = newHeight * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      newWidth,
      newHeight,
    );

    setImage(canvas.toDataURL(aspect === 1 ? "image/png" : file.type));
  };

  const getBlobFromCanvas = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            blob.name = file.name;
            blob.lastModified = file.lastModified;
            resolve(blob);
          } else {
            reject(new Error("Canvas is empty"));
          }
        },
        aspect === 1 ? "image/png" : file.type,
      );
    });

  useImperativeHandle(ref, () => ({
    hasImage: () => {
      return !!image;
    },
    clear: () => {
      setImage(null);
      fileInput.current.value = null;
    },
  }));

  function handleFileSelect(files, evt) {
    if (files.length === 0) return;

    setLoading(true);
    var reader = new FileReader();
    reader.onloadend = async function (e) {
      let imageData = e.target.result;
      if (aspect === 1) {
        imageData = await padImageToSquare(imageData);
      }
      setImage(imageData);
    };
    reader.readAsDataURL(files[0]);
    setFile(files[0]);
    setCropping(true);
  }

  const generateUpload = async () => {
    if (!completedCrop || !previewCanvasRef.current) {
      return;
    }
    if (onChange) {
      cropImage();
      const newFile = await getBlobFromCanvas(previewCanvasRef.current);
      onChange(newFile);
    }
    setCropping(false);
  };

  function onImageLoad(e) {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    if (aspect) {
      const percentCrop = centerAspectCrop(width, height, aspect);
      setCrop(percentCrop);
      setCompletedCrop({
        unit: "px",
        x: (percentCrop.x / 100) * width,
        y: (percentCrop.y / 100) * height,
        width: (percentCrop.width / 100) * width,
        height: (percentCrop.height / 100) * height,
      });
    }
    setLoading(false);
  }

  const onCloseCrop = () => {
    setImage(photo);
    setCropping(false);
    setLoading(false);
  };

  return (
    <div>
      {cropping && <div className="croppingBackdrop" onClick={onCloseCrop} />}
      <Box className={"croppingLayer" + (!cropping ? "" : " show")}>
        <Box gap="medium" align="center" pad="medium" overflow="scroll">
          <Box style={{ position: "relative" }}>
            {loading && (
              <Box
                align="center"
                justify="center"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <Spinner size="large" />
              </Box>
            )}
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              style={{
                maxHeight: 700,
                ...(label === "Logo" ? { background: "#333333" } : {}),
              }}
              aspect={aspect}
            >
              <img
                alt="preview"
                ref={imgRef}
                src={image}
                onLoad={onImageLoad}
                style={{
                  visibility: loading ? "hidden" : "visible",
                  maxHeight: 700,
                }}
              />
            </ReactCrop>
          </Box>
          <Box direction="row" justify="between" align="center" width="100%">
            <Button
              label={t("common.cancel")}
              color="brand"
              onClick={onCloseCrop}
            />
            <Text size="small" textAlign="center">
              {t("photoUploader.cropHelp")}
            </Text>
            <Button
              label={t("common.ok")}
              primary
              color="brand"
              onClick={generateUpload}
              disabled={
                !completedCrop || !completedCrop.width || !completedCrop.height
              }
            />
          </Box>
        </Box>
        <canvas
          ref={previewCanvasRef}
          style={{
            display: "none",
            width: Math.round((completedCrop && completedCrop.width) || 0),
            height: Math.round((completedCrop && completedCrop.height) || 0),
          }}
        />
      </Box>
      {label && <div className="label">{label}</div>}
      <div
        className={`home-image-loader ${fill ? "fill" : ""}`}
        style={{ width: width || 200, opacity: disabled ? 0.65 : 1 }}
        onClick={() => {
          if (!disabled) fileInput.current.click();
        }}
      >
        <FileDrop
          onDrop={(files, event) => !disabled && handleFileSelect(files, event)}
          onFrameDragEnter={(event) => setDropping(true)}
          onFrameDragLeave={(event) => setDropping(false)}
          onFrameDrop={(event) => setDropping(false)}
          className={`file-drop ${blackbg ? "blackbg" : ""}`}
        >
          {!image || dropping ? (
            <div className="drop-text">
              <strong>
                Drop a file here!
                <br />
                or click to browse files
              </strong>
            </div>
          ) : (
            <ImageGrommet src={image} className="image" />
          )}
        </FileDrop>
        <input
          type="file"
          id="files"
          ref={fileInput}
          disabled={disabled}
          className="file-input"
          onChange={(event) => handleFileSelect(event.target.files, event)}
        />
      </div>
    </div>
  );
}

export default forwardRef(PhotoUploader);
